"use client";
import React, { memo, useCallback, useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import {
  selectSectionTitles,
  setCurrentSection,
  selectCurrentSection,
  toggleSidebar,
  selectIsSidebarOpen,
} from "lib/redux/settingsSlice";
import { Tooltip } from "components/Tooltip";
import { capitalizeFirstLetterOfEachWord } from "lib/utils/capitalizeFirstLetterOfEachWord";
import { ExpanderWithHeightTransition } from "components/ExpanderWithHeightTransition";
import { UserIcon } from "@heroicons/react/24/outline";
import { BriefcaseIcon } from "@heroicons/react/24/outline";
import { AcademicCapIcon } from "@heroicons/react/24/outline";
import { FolderIcon } from "@heroicons/react/24/outline";
import { LightBulbIcon } from "@heroicons/react/24/outline";
import { AdjustmentsVerticalIcon } from "@heroicons/react/24/outline";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { cx } from "lib/cx";
import CustomIcon from "components/CustomIcon";

const sectionTitleIdToIcons: { [key: string]: React.ComponentType } = {
  personalDetails: UserIcon,
  workExperiences: BriefcaseIcon,
  educations: AcademicCapIcon,
  projects: FolderIcon,
  skills: LightBulbIcon,
  certifications: LightBulbIcon,
  languages: LightBulbIcon,
  awards: LightBulbIcon,
  custom: FolderIcon,
  resumeSettings: AdjustmentsVerticalIcon,
};

const Sidebar = () => {
  const currentSection = useAppSelector(selectCurrentSection);
  const sectionTitles = useAppSelector(selectSectionTitles);
  const isSideBarOpen = useAppSelector(selectIsSidebarOpen);
  const dispatch = useAppDispatch();

  const fixedSidebarTitles = sectionTitles.slice(0, 5);
  const dropDownTitles = sectionTitles.slice(5, 9);

  // Track whether currentSection belongs to either group so we open the
  // correct group on mount/when currentSection changes.
  const isCurrentSectionInDropDown = dropDownTitles.some(
    (title) => title.id === currentSection
  );
  const isCurrentSectionInFixed = fixedSidebarTitles.some(
    (title) => title.id === currentSection
  );

  // Only one group should be open at a time. Core should be open by
  // default on mount; More will open when a "more" section is active.
  const [moreOpen, setMoreOpen] = useState(false);
  const [coreOpen, setCoreOpen] = useState(true);

  // Keep group open state in sync with currentSection changes.
  useEffect(() => {
    if (isCurrentSectionInDropDown) {
      // If current section belongs to More, open More and close Core.
      setMoreOpen(true);
      setCoreOpen(false);
    } else {
      // Otherwise keep Core open and ensure More is closed.
      setCoreOpen(true);
      setMoreOpen(false);
    }
  }, [currentSection, isCurrentSectionInDropDown]);

  const handleMoreToggle = () => {
    const next = !moreOpen;
    setMoreOpen(next);
    if (next) setCoreOpen(false);
  };

  const handleCoreToggle = () => {
    const next = !coreOpen;
    setCoreOpen(next);
    if (next) setMoreOpen(false);
  };

  const handleSidebar = () => {
    dispatch(toggleSidebar());
  };

  const handleSectionChange = useCallback(
    (section: string) => {
      dispatch(setCurrentSection(section));

      // Ensure the correct group is open when selecting a section.
      const isFixed = fixedSidebarTitles.some((t) => t.id === section);
      const isMore = dropDownTitles.some((t) => t.id === section);
      if (isFixed) {
        setCoreOpen(true);
        setMoreOpen(false);
      } else if (isMore) {
        setMoreOpen(true);
        setCoreOpen(false);
      }
    },
    [dispatch, fixedSidebarTitles, dropDownTitles]
  );

  return (
    <div
      className={cx(
        "relative h-full pt-2.5",
        isSideBarOpen ? "w-48" : "w-20",
        "flex flex-col justify-between",
        "transition-[width] duration-500 ease-in-out"
      )}
    >
      <div
        className="text- absolute right-1 top-8 h-5 w-5 -translate-y-1/2 translate-x-1/2 cursor-pointer rounded-full bg-[#4E4E4E] p-[2px]"
        onClick={() => dispatch(toggleSidebar())}
      >
        {isSideBarOpen ? (
          <ChevronLeftIcon color="#FFFFFF" />
        ) : (
          <ChevronRightIcon color="#FFFFFF" />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <ul
          className={cx(
            "flex flex-col gap-1 rounded-3xl bg-white px-2 py-4 shadow",
            !isSideBarOpen ? "items-center" : "items-strech"
          )}
        >
          {/* Core Sections header */}
          <Tooltip text="Core Sections" position="right" visible={!isSideBarOpen}>
            <li
              className={cx(
                "h-8 cursor-pointer rounded-2xl text-sm",
                "flex items-center gap-2",
                !isSideBarOpen ? "w-8 justify-center rounded-full" : "px-[14px] py-4",
                coreOpen && "bg-[#2D2D2D] text-white"
              )}
              onClick={handleCoreToggle}
            >
              <div className="h-5 w-5">{coreOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}</div>
              <div
                className={cx(
                  "opacity-0",
                  !isSideBarOpen ? "hidden max-w-0" : "max-w-full opacity-100",
                  "whitespace-nowrap",
                  "overflow-hidden transition-all duration-500 ease-in-out"
                )}
              >
                Core Sections
              </div>
            </li>
          </Tooltip>

          <ExpanderWithHeightTransition expanded={coreOpen} overflowHidden>
            <ul className=" flex w-full flex-col gap-1 rounded-md">
              {fixedSidebarTitles.map((title, index) => {
                const Icon = sectionTitleIdToIcons[title.id];
                return (
                  <Tooltip
                    text={title.title}
                    key={title.id}
                    position="right"
                    visible={!isSideBarOpen}
                  >
                    <li
                      key={index}
                      className={cx(
                        "h-8 cursor-pointer rounded-2xl text-sm",
                        "flex items-center gap-2",
                        currentSection === title.id && "bg-[#4198FF] text-white",
                        !isSideBarOpen
                          ? "w-8 justify-center rounded-full p-[18px]"
                          : "px-[14px] py-4"
                      )}
                      onClick={() => handleSectionChange(title.id)}
                      data-tooltip-target="target-tooltip"
                    >
                      <div className="h-5 w-5">
                        <CustomIcon
                          iconName={title.id}
                          stroke={`${currentSection === title.id ? "white" : "black"}`}
                        />
                      </div>
                      <div
                        className={cx(
                          "opacity-0",
                          !isSideBarOpen ? "hidden max-w-0" : "max-w-full opacity-100",
                          "whitespace-nowrap",
                          "overflow-hidden transition-all duration-500 ease-in-out"
                        )}
                      >
                        {capitalizeFirstLetterOfEachWord(title.title)}
                      </div>
                    </li>
                  </Tooltip>
                );
              })}
            </ul>
          </ExpanderWithHeightTransition>

          {/* More Sections header */}
          <Tooltip text="More Sections" position="right" visible={!isSideBarOpen}>
            <li
              className={cx(
                "h-8 cursor-pointer rounded-2xl text-sm",
                "flex items-center gap-2",
                !isSideBarOpen ? "w-8 justify-center rounded-full" : "px-[14px] py-4",
                moreOpen && "bg-[#2D2D2D] text-white"
              )}
              onClick={handleMoreToggle}
            >
              <div className="h-5 w-5">{moreOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}</div>
              <div
                className={cx(
                  "opacity-0",
                  !isSideBarOpen ? "hidden max-w-0" : "max-w-full opacity-100",
                  "whitespace-nowrap",
                  "overflow-hidden transition-all duration-500 ease-in-out"
                )}
              >
                More Sections
              </div>
            </li>
          </Tooltip>

          <ExpanderWithHeightTransition expanded={moreOpen} overflowHidden>
            <ul className=" flex w-full flex-col gap-1 rounded-md">
              {dropDownTitles.map((title) => {
                const Icon = sectionTitleIdToIcons[title.id];
                return (
                  <Tooltip
                    text={title.title}
                    key={title.id}
                    position="right"
                    visible={!isSideBarOpen}
                  >
                    <li
                      className={cx(
                        "h-8 cursor-pointer rounded-2xl text-sm",
                        "flex items-center gap-2",
                        currentSection === title.id && "bg-[#4198FF] text-white",
                        !isSideBarOpen
                          ? "w-8 justify-center rounded-full p-[18px]"
                          : "px-[14px] py-4"
                      )}
                      onClick={() => {
                        handleSectionChange(title.id);
                      }}
                    >
                      <div className="h-5 w-5">
                        <CustomIcon
                          iconName={title.id}
                          stroke={`${currentSection === title.id ? "white" : "black"}`}
                        />
                      </div>
                      <div
                        className={cx(
                          "opacity-0",
                          !isSideBarOpen ? "hidden max-w-0" : "max-w-full opacity-100",
                          "whitespace-nowrap",
                          "overflow-hidden transition-all duration-500 ease-in-out"
                        )}
                      >
                        {capitalizeFirstLetterOfEachWord(title.title)}
                      </div>
                    </li>
                  </Tooltip>
                );
              })}
            </ul>
          </ExpanderWithHeightTransition>
        </ul>

        <ul
          className={cx(
            "flex flex-col gap-1 rounded-3xl bg-white px-2 py-4 lg:py-2 shadow",
            !isSideBarOpen ? "items-center" : "items-stretch"
          )}
        >
          <Tooltip
            text={"Customize Resume"}
            position="right"
            visible={!isSideBarOpen}
          >
            <li
              className={cx(
                "h-8 cursor-pointer rounded-2xl text-sm",
                "flex items-center gap-2",
                currentSection === "resumeSettings" &&
                  "bg-[#4198FF] text-white",
                !isSideBarOpen
                  ? "w-8 justify-center rounded-full p-[18px]"
                  : "px-[14px] py-4"
              )}
              onClick={() => handleSectionChange("resumeSettings")}
              data-tooltip-target="target-tooltip"
            >
              <div className="h-5 w-5">
                {/* <AdjustmentsVerticalIcon /> */}
                <CustomIcon
                  iconName="settings"
                  stroke={`${
                    currentSection === "resumeSettings" ? "white" : "black"
                  }`}
                />
              </div>
              <div
                className={cx(
                  "opacity-0",
                  !isSideBarOpen ? "hidden max-w-0" : "max-w-full opacity-100",
                  "overflow-hidden transition-all duration-500 ease-in-out"
                )}
              >
                Customize
              </div>
            </li>
          </Tooltip>

          {/* Resume Template Option */}
          <Tooltip
            text="Resume Templates"
            position="right"
            visible={!isSideBarOpen}
          >
            <li
              className={cx(
                "h-8 cursor-pointer rounded-2xl text-sm",
                "flex items-center gap-2",
                currentSection === "templates" && "bg-[#4198FF] text-white",
                !isSideBarOpen
                  ? "w-8 justify-center rounded-full p-[18px]"
                  : "px-[14px] py-4 "
              )}
              onClick={() => handleSectionChange("templates")}
            >
              {/* <Squares2X2Icon className="h-5 w-5" /> */}
              <div className="h-5 w-5">
                <CustomIcon
                  iconName="templates"
                  stroke={`${
                    currentSection === "templates" ? "white" : "black"
                  }`}
                />
              </div>

              <div
                className={cx(
                  "opacity-0",
                  !isSideBarOpen ? "hidden max-w-0" : "max-w-full opacity-100",
                  "overflow-hidden transition-all duration-500 ease-in-out"
                )}
              >
                Templates
              </div>
            </li>
          </Tooltip>
        </ul>
      </div>
    </div>
  );
};

export default memo(Sidebar);
